// ── Bhookly AI: files attached to a message (F-AI-62) ──────────────────────
// What the widget accepts and how an image is prepared before upload. The
// backend checks every file again (handlers/ai_attachments.go); this side
// only gives quick feedback and keeps photos small.

/** The file picker's filter. */
export const ATTACH_ACCEPT = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  '.pdf',
  '.xlsx',
  '.xlsm',
  '.csv',
  '.tsv',
  '.docx',
  '.txt',
  '.md',
  '.json',
].join(',')

export const MAX_FILES_PER_MESSAGE = 5
export const MAX_FILE_BYTES = 10 * 1024 * 1024

/**
 * Images are sent at most this many pixels on the long edge: the model reads
 * nothing more from a bigger one, and a phone photo shrinks from megabytes to
 * a few hundred kilobytes.
 */
const MAX_IMAGE_EDGE = 1568
/** Below this an image is sent as it is (unless it is too big in pixels). */
const IMAGE_PASS_BYTES = 1.5 * 1024 * 1024
const READABLE_IMAGES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif'])

export type AttachmentKind = 'image' | 'pdf' | 'text'

/** The kind the copilot will read the file as, from its name and type. */
export function attachmentKind(file: { name: string; type: string }): AttachmentKind {
  if (file.type.startsWith('image/') || /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(file.name)) return 'image'
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) return 'pdf'
  return 'text'
}

/** A sentence for a file the widget refuses before uploading, or null. */
export function precheckFile(file: File): string | null {
  if (file.size === 0) return 'That file is empty.'
  if (/\.(xls|doc|ppt|pptx)$/i.test(file.name)) {
    return 'Old Office files can’t be read — save it as .xlsx, .docx or PDF and attach that.'
  }
  if (file.size > MAX_FILE_BYTES && attachmentKind(file) !== 'image') {
    return 'That file is too large — attach one under 10 MB.'
  }
  return null
}

/** "2.4 MB", "820 KB". */
export function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(n / 1024))} KB`
}

function isHeic(file: File): boolean {
  return /image\/hei[cf]/i.test(file.type) || /\.(heic|heif)$/i.test(file.name)
}

/**
 * The file as it will be uploaded: an image larger than the model needs (or
 * in a format it can't read, like iPhone HEIC — when this browser can open
 * it) is redrawn as a JPEG at most MAX_IMAGE_EDGE on its long edge. Anything
 * else, or an image the browser can't decode, is returned unchanged.
 */
export async function prepareForUpload(file: File): Promise<{ blob: Blob; name: string }> {
  if (attachmentKind(file) !== 'image') return { blob: file, name: file.name }
  const heic = isHeic(file)
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    return { blob: file, name: file.name } // the backend explains what it can't read
  }
  try {
    const edge = Math.max(bitmap.width, bitmap.height)
    const fine = READABLE_IMAGES.has(file.type) && file.size <= IMAGE_PASS_BYTES && edge <= MAX_IMAGE_EDGE
    // A GIF is kept as it is when it fits: redrawing would drop its animation for nothing.
    if (fine || (file.type === 'image/gif' && file.size <= IMAGE_PASS_BYTES)) return { blob: file, name: file.name }
    const scale = Math.min(1, MAX_IMAGE_EDGE / edge)
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    const ctx = canvas.getContext('2d')
    if (!ctx) return { blob: file, name: file.name }
    // A PNG screenshot keeps sharp text as PNG; photos become JPEG.
    const png = file.type === 'image/png' && !heic
    if (!png) {
      ctx.fillStyle = '#fff' // transparent areas would turn black in a JPEG
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    const type = png ? 'image/png' : 'image/jpeg'
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.85))
    if (!blob) return { blob: file, name: file.name }
    const base = file.name.replace(/\.[^.]+$/, '') || 'image'
    return { blob, name: png ? file.name : `${base}.jpg` }
  } finally {
    bitmap.close()
  }
}

/** A name for an image pasted from the clipboard, which has none of its own. */
export function pastedImageName(type: string, index: number): string {
  const ext = type === 'image/jpeg' ? 'jpg' : (type.split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '')
  const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '')
  return `screenshot-${stamp}${index ? `-${index + 1}` : ''}.${ext}`
}
